'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { membershipService } from '@/lib/services/membershipService';

interface MembershipTier {
  id: string;
  name: string;
  price: string;
  description: string;
  features: string[];
  responsibilities: string[];
  isPopular?: boolean;
}

const MembershipSection: React.FC = () => {
  const [selectedTier, setSelectedTier] = useState<MembershipTier | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });

  const membershipTiers: MembershipTier[] = [
    {
      id: 'peace-seeker',
      name: 'Peace Seeker',
      price: 'Free',
      description: 'Begin your journey with essential resources',
      features: [
        'Access to basic preparation materials',
        'Monthly newsletter',
        'Community forum access',
        'April 17th celebration guide'
      ],
      responsibilities: [
        'Complete personal reflection exercises',
        'Participate in online discussions',
        'Share your transformation story'
      ]
    },
    {
      id: 'peace-builder',
      name: 'Peace Builder',
      price: '$10',
      description: 'Deepen your commitment with enhanced support',
      features: [
        'All Peace Seeker benefits',
        'Weekly guided meditations',
        'Advanced forgiveness frameworks',
        'Priority event registration',
        'Exclusive webinar access'
      ],
      responsibilities: [
        'Attend monthly virtual gatherings',
        'Mentor new community members',
        'Contribute to resource library'
      ],
      isPopular: true
    },
    {
      id: 'peace-champion',
      name: 'Peace Champion',
      price: '$50',
      description: 'Lead the movement with comprehensive resources',
      features: [
        'All Peace Builder benefits',
        'One-on-one coaching sessions',
        'Ambassador certification program',
        'Co-branding opportunities',
        'Direct founder access'
      ],
      responsibilities: [
        'Organize local chapter events',
        'Recruit and train volunteers',
        'Report quarterly impact metrics'
      ]
    }
  ];

  const handleSelectTier = (tier: MembershipTier) => {
    console.log('Membership tier selected:', tier.name);
    setSelectedTier(tier);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Membership form submitted');
    if (!selectedTier) return;

    setLoading(true);
    try {
      const result = await membershipService.register({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        tierId: selectedTier.id,
        tierName: selectedTier.name,
        tierPrice: selectedTier.price,
        message: formData.message
      });

      if (result) {
        alert(`Membership Registration Successful!\n\nThank you, ${formData.fullName}!\n\nYou've successfully registered for the ${selectedTier.name} tier.\n\nWe'll send a confirmation email to ${formData.email} with next steps and access details.\n\nWelcome to the Global Peace Movement!`);
        setShowForm(false);
        setFormData({ fullName: '', email: '', phone: '', message: '' });
        setSelectedTier(null);
      }
    } catch (error) {
      alert('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Join the Global Peace Movement
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose your membership tier and become part of a worldwide community dedicated to peace, forgiveness, and healing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {membershipTiers.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-card rounded-lg p-6 border transition-all duration-300 hover:shadow-lg ${
                tier.isPopular ? 'border-accent shadow-md' : 'border-border'
              }`}
            >
              {tier.isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-accent text-accent-foreground px-4 py-1 rounded-full text-xs font-bold">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-headline font-bold text-foreground mb-2">{tier.name}</h3>
                <div className="mb-3">
                  <span className="text-4xl font-bold text-primary">{tier.price}</span>
                  {tier.price !== 'Free' && <span className="text-muted-foreground">/month</span>}
                </div>
                <p className="text-sm text-muted-foreground">{tier.description}</p>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="SparklesIcon" size={16} className="text-primary mr-2" />
                  Benefits
                </h4>
                <ul className="space-y-2">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="CheckIcon" size={16} className="text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="HandRaisedIcon" size={16} className="text-secondary mr-2" />
                  Responsibilities
                </h4>
                <ul className="space-y-2">
                  {tier.responsibilities.map((responsibility, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon
                        name="ArrowRightIcon"
                        size={16}
                        className="text-muted-foreground flex-shrink-0 mt-0.5"
                      />
                      <span className="text-sm text-muted-foreground">{responsibility}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => handleSelectTier(tier)}
                className={`w-full px-6 py-3 rounded-md font-cta text-sm font-bold transition-all duration-300 hover:shadow-md ${
                  tier.isPopular
                    ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
              >
                Choose {tier.name}
              </button>
            </div>
          ))}
        </div>
      </div>

      {showForm && selectedTier && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-card rounded-lg max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowForm(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              aria-label="Close form"
            >
              <Icon name="XMarkIcon" size={24} />
            </button>

            <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
              Register for {selectedTier.name}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {selectedTier.price}/month - {selectedTier.description}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message (Optional)
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Tell us why you want to join..."
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 border border-border rounded-md text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md font-bold hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Registering...' : 'Register'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default MembershipSection;